/**
 * MARKETS_SEED — static seed data for Phase 3 Hero + Markets sections.
 *
 * Schema is forward-compatible with Phase 4's Zod MarketResponse mapper.
 * Phase 4 will replace this import with useMarkets() — do NOT add Zod,
 * useQuery, or skeleton/error/empty states here (those are MARKETS-01..05).
 *
 * Field names match what frontend/lib/api/schemas/market.ts produces after
 * the marketMapper (camelCase, EUR amounts, ISO timestamps).
 *
 * lastPriceYes + lastPriceNo === 1.00 for every entry.
 */

export type MarketSeed = {
  id: string;
  title: string;
  sport: string;
  league: string | null;
  status: 'active' | 'closed' | 'resolved';
  lastPriceYes: number; // 0..1 (e.g., 0.62 for 62¢)
  lastPriceNo: number; // 1 - lastPriceYes
  volumeEur: number; // total trading volume in EUR
  eventStart: string; // ISO 8601 timestamp
  live: boolean; // true if event is currently live
  closeLabel: string; // localized "Closes" label, e.g. "Затвара 23:00"
  category: string; // top-of-card label, e.g. "ФУДБАЛ · МК"
};

export const MARKETS_SEED: ReadonlyArray<MarketSeed> = [
  {
    id: 'mkt-vardar-shkendija',
    title: 'Дали Вардар ќе го победи Шкендија на дербито?',
    sport: 'football',
    league: 'Macedonian Prva Liga',
    status: 'active',
    lastPriceYes: 0.62,
    lastPriceNo: 0.38,
    volumeEur: 84210,
    eventStart: '2026-05-15T20:00:00Z',
    live: true,
    closeLabel: 'Затвара 23:00',
    category: 'ФУДБАЛ · МК',
  },
  {
    id: 'mkt-ecb-rate-june',
    title: 'Дали ЕЦБ ќе ја намали каматата до јунската седница?',
    sport: 'politics',
    league: null,
    status: 'active',
    lastPriceYes: 0.41,
    lastPriceNo: 0.59,
    volumeEur: 312400,
    eventStart: '2026-06-06T12:00:00Z',
    live: false,
    closeLabel: 'Затвара 6 јуни',
    category: 'ПОЛИТИКА · ЕУ',
  },
  {
    id: 'mkt-f1-verstappen-2026',
    title: 'Дали Верстапен ќе ја освои титулата за 2026?',
    sport: 'f1',
    league: 'Formula 1',
    status: 'active',
    lastPriceYes: 0.54,
    lastPriceNo: 0.46,
    volumeEur: 1200000,
    eventStart: '2026-12-13T14:00:00Z',
    live: true,
    closeLabel: 'Затвара 13 дек',
    category: 'F1',
  },
  {
    id: 'mkt-skopje-rain-sat',
    title: 'Дали ќе врне дожд во Скопје во сабота?',
    sport: 'weather',
    league: null,
    status: 'active',
    lastPriceYes: 0.74,
    lastPriceNo: 0.26,
    volumeEur: 18902,
    eventStart: '2026-05-10T18:00:00Z',
    live: false,
    closeLabel: 'Затвара 18:00 саб',
    category: 'ВРЕМЕ · СКОПЈЕ',
  },
  {
    id: 'mkt-btc-q2-100k',
    title: 'Дали BTC ќе го затвори Q2 над 100.000$?',
    sport: 'digital-assets',
    league: null,
    status: 'active',
    lastPriceYes: 0.33,
    lastPriceNo: 0.67,
    volumeEur: 642808,
    eventStart: '2026-06-30T22:00:00Z',
    live: true,
    closeLabel: 'Затвара 30 јуни',
    category: 'КРИПТО',
  },
  {
    id: 'mkt-eurovision-mk-top10',
    title: 'Дали Македонија ќе влезе во топ 10 на Евровизија?',
    sport: 'culture',
    league: 'Eurovision',
    status: 'active',
    lastPriceYes: 0.12,
    lastPriceNo: 0.88,
    volumeEur: 7420,
    eventStart: '2026-05-10T20:00:00Z',
    live: false,
    closeLabel: 'Затвара 10 мај',
    category: 'ЕВРОВИЗИЈА',
  },
];
